import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { useMemo } from 'react';

import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { Distance } from '@/types/Distance';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { FilterAction } from '@/types/SearchFacet';
import { FirstTimeWalkthroughSteps } from '@/types/walkthroughs';

import { NoFilterTermResults } from './components/NoFilterTermResults/NoFilterTermResults';
import { SearchFilterChips } from './components/SearchFilterChips/SearchFilterChips';
import { SearchFilters } from './components/SearchFilters/SearchFilters';
import { SearchPageCardGrid } from './components/SearchPageCardGrid/SearchPageCardGrid';
enum CurrentTab {
    Archive = 'archive',
    Online = 'online',
}

type Props = {
    distance: Distance;
    hasNoFilterTermOrRangeResults: boolean;
    hideTabNavigation?: boolean;
    isArchivedSearch: boolean;
    isSEOSearchPage: boolean;
    searchTerm: string;
    totalSoldItems: number;
};

export const SearchPageTabs = ({
    distance,
    hasNoFilterTermOrRangeResults,
    hideTabNavigation = false,
    isArchivedSearch,
    isSEOSearchPage,
    searchTerm,
    totalSoldItems,
}: Props) => {
    const currentTab = isArchivedSearch ? CurrentTab.Archive : CurrentTab.Online;

    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleNavigate = (status: CurrentTab) => {
        if (isSEOSearchPage) {
            return;
        }

        navigateWithParams({
            filterAction:
                status === CurrentTab.Archive ? FilterAction.ViewPriceResults : FilterAction.ViewUpcomingItems,
            params: {
                keyword: searchTerm,
                sort: SearchSortAndDirection.MostRelevant,
                status,
            },
            preventScrollReset: true,
        });
    };

    const tabs = useMemo(() => {
        const onlineTab = {
            content: (
                <>
                    <SearchFilters
                        distance={distance}
                        searchTerm={searchTerm}
                    />
                    <SearchFilterChips />
                    {hasNoFilterTermOrRangeResults ? (
                        <NoFilterTermResults
                            isArchivedSearch={isArchivedSearch}
                            searchTerm={searchTerm}
                            totalSoldItems={totalSoldItems}
                        />
                    ) : (
                        <SearchPageCardGrid />
                    )}
                </>
            ),
            contentId: CurrentTab.Online,
            label: <FormattedMessage id="search_facets.upcoming_status_label" />,
        };

        const archiveTab = {
            ...onlineTab,
            contentId: CurrentTab.Archive,
            label: (
                <span data-walkthrough-step={FirstTimeWalkthroughSteps.Search}>
                    <FormattedMessage id="search_facets.price_results_status_label" />
                </span>
            ),
        };

        // If we're on an SEO search page, we only show the online tab
        if (isSEOSearchPage) {
            return [onlineTab];
        }

        return [onlineTab, archiveTab];
    }, [distance, hasNoFilterTermOrRangeResults, isArchivedSearch, isSEOSearchPage, searchTerm, totalSoldItems]);

    if (hideTabNavigation) {
        return (
            <>
                <SearchFilters
                    distance={distance}
                    searchTerm={searchTerm}
                />
                <SearchFilterChips />
                {hasNoFilterTermOrRangeResults ? (
                    <NoFilterTermResults
                        isArchivedSearch={isArchivedSearch}
                        searchTerm={searchTerm}
                        totalSoldItems={totalSoldItems}
                    />
                ) : (
                    <SearchPageCardGrid />
                )}
            </>
        );
    }

    return (
        <Tabs
            currentTab={currentTab}
            keepTabsInDOM={false}
            onTabChanged={handleNavigate}
            tabs={tabs}
        />
    );
};
